import { RentalGrowthListingWebApiHandler_getByAddress } from '@zg-rentals/ts-rental-growth-api-client';
import type {
  FloorplanDto,
  GetListingByAddressListingFilterInputDto,
  GetListingByAddressRequestDto,
  GetListingByAddressResponseDto,
} from '@zg-rentals/ts-rental-growth-api-client';

// Actions
import { setActiveId } from './UIActions';

// Models
import Address from '../../models/Address';
import Comparables from '../../models/Comparables';

// Misc / utils
import { augmentError } from '../../utils/errors';
import constants from '../constants';
import errorConstants from '../../constants/ErrorConstants';
import type { FilterParamsType } from '../../utils/queries';
import type { AppThunkAddress, AppThunkPromise } from '../types';
import { emitErrorEvent } from '../../utils/analytics/ClickstreamEvents';

// ActionCreators... <verb><Noun>
export const getComparables = (params: GetListingByAddressRequestDto): AppThunkPromise => {
  return async (dispatch) => {
    const { data } = await dispatch(fetchListingByAddress(params));
    const userComps = dispatch(setComparables(data));

    return { userComps };
  };
};

export const getAddressAndComparables = (params: GetListingByAddressRequestDto): AppThunkPromise => {
  return async (dispatch) => {
    const { data } = await dispatch(fetchListingByAddress(params));
    const userAddress = dispatch(setAddress(data));
    const hasZestimate = Boolean(data.floorplans?.[0]?.zestimate?.rentZestimate);
    const hasCompsPrices = Boolean(data.similarFloorplans?.floorplans?.length);

    if (!hasCompsPrices && !hasZestimate) {
      throwNonCriticalError({
        message: "We don't have enough data for this address",
        code: errorConstants.RENT_ZESTIMATE_NOT_FOUND,
      });
    }

    if (hasCompsPrices) {
      await dispatch(setComparables(data));
    }

    return {
      userAddress,
    };
  };
};

export const buildSimilarFloorplansFilter = (params: FilterParamsType): GetListingByAddressListingFilterInputDto => {
  const {
    activeTypes,
    activatedDays,
    amenities,
    bedrooms,
    deactivatedDays,
    distanceInMiles,
    laundry,
    limit,
    offset,
    orderBy,
    pets,
    price,
    propertyTypes,
  } = params;

  return {
    orderBy: orderBy || 'lowPrice',
    ...(activeTypes && { activeTypes }),
    ...(activatedDays && { activatedDays }),
    ...(amenities && { amenities }),
    ...(bedrooms && { bedrooms }),
    ...(deactivatedDays && { deactivatedDays }),
    ...(distanceInMiles && { distanceInMiles }),
    ...(laundry && { laundry }),
    ...(limit && { limit }),
    ...(offset && { offset }),
    ...(pets && { pets }),
    ...(price && { price }),
    ...(propertyTypes && { propertyTypes }),
  };
};

export const fetchListingByAddress = (params: GetListingByAddressRequestDto) => {
  return async (dispatch, getState) => {
    try {
      const user = getState().user;
      const response = await RentalGrowthListingWebApiHandler_getByAddress(params);

      if (!response.floorplans?.length) {
        emitErrorEvent({ user });
        throwNonCriticalError({
          message: 'We were unable to match this address',
          code: errorConstants.PROPERTY_DETAILS_NOT_FOUND,
        });
      }
      if (response.similarFloorplansError || !response.similarFloorplans?.floorplans?.length) {
        emitErrorEvent({ user });
      }
      if (!response.floorplans?.[0]?.zestimate?.rentZestimate) {
        emitErrorEvent({ user });
      }

      return { data: response };
    } catch (error) {
      augmentError(error, {
        action: 'Error in AddressActions#fetchListingByAddress',
      });

      throw error;
    }
  };
};

// Setters
export const setAddress = (data: GetListingByAddressResponseDto): AppThunkAddress => {
  return (dispatch) => {
    const addressData = {
      ...data.floorplans![0],
      geo: data.geo,
      marketSummary: data.marketSummary,
    };
    const modeledAddressData = new Address(addressData);

    dispatch({
      type: constants.SET_ADDRESS,
      payload: modeledAddressData,
    });

    modeledAddressData.listingId && dispatch(setActiveId(modeledAddressData.listingId));

    return modeledAddressData;
  };
};

export const setComparables = (data: GetListingByAddressResponseDto) => {
  return (dispatch, getState) => {
    const similarFloorplans = data.similarFloorplans;
    if (!similarFloorplans) {
      return [];
    }

    const { floorplans, listingRanking, lowestPriceIncluded: min, highestPriceIncluded: max } = similarFloorplans;

    const compsItems = floorplans.map((comp: FloorplanDto) => {
      return new Comparables(comp);
    });

    const userProperty = getState().address;
    compsItems.splice(listingRanking, 0, userProperty);
    const userPropertyIdx = compsItems.findIndex((comp) => comp.isUsersComp);

    dispatch({
      type: constants.SET_COMPARABLES,
      payload: {
        min,
        max,
        items: compsItems,
        userPropertyIdx,
      },
    });

    dispatch(setActiveId(userProperty.listingId));

    return compsItems;
  };
};

// Errors
const throwNonCriticalError = ({ message, code }: { message: string; code: string }) => {
  throw {
    nonCriticalError: true,
    errorCode: code,
    errorMsg: message,
  };
};
